import React from 'react';
import { ScrollLink } from './ScrollLink';
import useSEO from '../hooks/useSEO';
import '../styles/CertificadosPage.css';

const Check = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

const CertificadosPage = () => {
  useSEO({
    title: 'Certificado Digital + Currículo Profissional | ABRALIBER',
    description: 'Conclua um curso gratuito e escolha seu certificado digital verificável ou o Kit Carreira com currículo profissional.',
    url: '/certificados',
    keywords: 'certificado digital, curso livre, currículo profissional, ABRALIBER',
  });

  return (
    <main className="certificados-page">
      <section className="certificados-hero">
        <div className="certificados-hero-inner">
          <div className="certificados-hero-copy">
            <span className="certificados-eyebrow">CERTIFICADO + CURRÍCULO</span>
            <h1>Transforme o curso concluído em uma apresentação profissional.</h1>
            <p>
              Estude gratuitamente. Ao concluir, escolha o certificado digital verificável
              ou leve o Kit Carreira, com currículo profissional pronto para personalizar.
            </p>
            <div className="certificados-hero-actions">
              <ScrollLink to="/cursos" className="certificados-primary-action">
                Encontrar meu curso
                <span aria-hidden="true">→</span>
              </ScrollLink>
              <a href="#como-funciona" className="certificados-text-action">Como funciona</a>
            </div>
            <ul className="certificados-trust-list">
              <li><Check /> Cursos 100% gratuitos</li>
              <li><Check /> Emissão digital</li>
              <li><Check /> Verificação de autenticidade</li>
            </ul>
          </div>

          <div className="career-kit-preview" aria-label="Prévia do Kit Carreira">
            <div className="career-kit-orbit career-kit-orbit-one" />
            <div className="career-kit-orbit career-kit-orbit-two" />
            <div className="career-kit-document career-kit-certificate">
              <span className="document-kicker">ABRALIBER</span>
              <strong>Certificado</strong>
              <span className="document-name">Seu nome aqui</span>
              <div className="document-lines"><i /><i /><i /></div>
              <span className="document-seal">✓</span>
            </div>
            <div className="career-kit-document career-kit-resume">
              <span className="resume-avatar">SN</span>
              <strong>Seu Nome</strong>
              <small>Perfil profissional</small>
              <div className="document-lines"><i /><i /><i /><i /></div>
              <span className="resume-course">Curso ABRALIBER incluído</span>
            </div>
            <span className="career-kit-badge">Kit Carreira</span>
          </div>
        </div>
      </section>

      <section className="certificados-intro" aria-label="Diferenciais">
        <article><strong>+400</strong><span>cursos para escolher</span></article>
        <article><strong>100%</strong><span>online e no seu ritmo</span></article>
        <article><strong>24h</strong><span>de acesso à plataforma</span></article>
        <article><strong>Brasil</strong><span>certificado verificável</span></article>
      </section>

      <section className="certificados-section" id="como-funciona">
        <div className="certificados-section-heading">
          <span>UMA JORNADA SIMPLES</span>
          <h2>Do aprendizado à oportunidade</h2>
          <p>Você estuda sem custo e decide sobre o certificado apenas depois de concluir.</p>
        </div>
        <div className="career-steps">
          <article><b>01</b><h3>Escolha um curso</h3><p>Encontre a área que combina com seu objetivo profissional.</p></article>
          <article><b>02</b><h3>Estude gratuitamente</h3><p>Acesse o conteúdo online e avance no seu próprio ritmo.</p></article>
          <article><b>03</b><h3>Conclua a avaliação</h3><p>Finalize o curso e libere as opções de emissão disponíveis.</p></article>
          <article><b>04</b><h3>Apresente sua conquista</h3><p>Use o certificado e mantenha seu currículo pronto para enviar.</p></article>
        </div>
      </section>

      <section className="certificados-section kit-section">
        <div className="certificados-section-heading">
          <span>ESCOLHA DEPOIS DE CONCLUIR</span>
          <h2>Um documento ou um kit para sua carreira</h2>
          <p>Compare as opções e escolha a que faz mais sentido para o seu momento.</p>
        </div>
        <div className="kit-comparison">
          <article className="kit-card">
            <span className="kit-card-label">Essencial</span>
            <h3>Certificado Digital</h3>
            <p className="kit-card-description">Para registrar e comprovar a conclusão do seu curso livre.</p>
            <ul>
              <li><Check /> Documento digital de conclusão</li>
              <li><Check /> Código para verificar autenticidade</li>
              <li><Check /> Download rápido e seguro</li>
            </ul>
            <ScrollLink to="/cursos" className="kit-card-action kit-card-action-secondary">Ver cursos</ScrollLink>
          </article>

          <article className="kit-card kit-card-featured">
            <span className="kit-popular">MAIS COMPLETO</span>
            <span className="kit-card-label">Kit Carreira</span>
            <h3>Certificado + Currículo</h3>
            <p className="kit-card-description">Sua conquista apresentada em um currículo profissional e sempre atualizável.</p>
            <ul>
              <li><Check /> Tudo do certificado digital</li>
              <li><Check /> Curso incluído automaticamente</li>
              <li><Check /> Currículo editável sempre que precisar</li>
              <li><Check /> Download para enviar às empresas</li>
            </ul>
            <ScrollLink to="/cursos" className="kit-card-action">Começar um curso grátis</ScrollLink>
          </article>
        </div>
      </section>

      <section className="certificate-proof">
        <div>
          <span className="certificados-eyebrow">CLAREZA E CONFIANÇA</span>
          <h2>Seu certificado, fácil de apresentar e verificar.</h2>
          <p>
            Os certificados ABRALIBER identificam o aluno, o curso e a carga horária,
            com validação digital de autenticidade.
          </p>
          <ul>
            <li><Check /> Ideal para processos seletivos e desenvolvimento profissional</li>
            <li><Check /> Pode apoiar a comprovação de atividades complementares</li>
            <li><Check /> Emitido após os requisitos de conclusão do curso</li>
          </ul>
          <small>
            A aceitação do certificado depende das regras da instituição, empresa,
            edital ou finalidade em que ele será apresentado.
          </small>
        </div>
        <div className="certificate-paper" aria-hidden="true">
          <span>ABRALIBER</span>
          <strong>Certificado de Conclusão</strong>
          <i />
          <b>NOME DO ALUNO</b>
          <p>concluiu o curso livre com aproveitamento</p>
          <div><em>Carga horária</em><em>Código verificável</em></div>
        </div>
      </section>

      <section className="certificados-final-cta">
        <div>
          <span>SEU PRÓXIMO PASSO COMEÇA AGORA</span>
          <h2>Escolha um curso. O estudo é gratuito.</h2>
          <p>Você só decide sobre o certificado e o Kit Carreira depois da conclusão.</p>
        </div>
        <ScrollLink to="/cursos" className="certificados-primary-action">Explorar cursos <span>→</span></ScrollLink>
      </section>
    </main>
  );
};

export default CertificadosPage;
